import React from "react";
import Reveal from "../site/Reveal";
import { SectionLabel, ArrowLink } from "../site/Bits";
import { FOUNDERS, FOUNDERS_SECTION, COLLABORATION } from "../../lib/content";

const FounderProfile = ({ founder, index }) => (
  <Reveal variant="image" delay={index * 0.1}>
    <figure data-testid="founder-profile" className="relative">
      {/* soft light blue mat frames both portraits identically */}
      <div
        className="relative w-full max-w-[290px]"
        style={{
          background: "var(--lykit-mist)",
          border: "1px solid var(--lykit-mist-edge)",
          padding: "clamp(9px,0.9vw,13px)",
        }}
        data-testid="founder-portrait-mat"
      >
        <div className="img-frame img-zoom relative" style={{ aspectRatio: "4 / 5" }}>
          <img
            src={founder.image}
            alt={`${founder.name}, ${founder.role} at LYKIT Entertainment`}
            className="founder-grade"
            loading="lazy"
          />
        </div>
      </div>

      <figcaption className="mt-5">
        <span className="project-meta block" style={{ color: "rgba(23,21,34,0.4)" }}>
          {founder.num}
        </span>
        <h3 className="display-sm mt-2" data-testid="founder-name">
          {founder.name}
        </h3>
        <p className="project-meta mt-2" style={{ color: "rgba(23,21,34,0.56)" }}>
          {founder.role}
        </p>
        <span className="gold-rule mt-3" style={{ width: 24 }} />
        <p className="body-muted mt-4 text-[14px] leading-[1.65]" data-testid="founder-bio">
          {founder.bio}
        </p>
      </figcaption>
    </figure>
  </Reveal>
);

export default function Founders() {
  return (
    <section className="lk-section-tight" data-testid="founders-section" id="founders">
      <div className="lk-container-wide">
        {/* header + subheading + intro */}
        <div className="grid gap-y-7 lg:grid-cols-12 lg:gap-x-14">
          <div className="lg:col-span-6">
            <Reveal>
              <SectionLabel>Leadership</SectionLabel>
            </Reveal>
            <Reveal delay={0.08}>
              <h2 className="display-lg mt-5 max-w-[20ch]" data-testid="founders-header">
                {FOUNDERS_SECTION.header}
              </h2>
            </Reveal>
          </div>
          <div className="lg:col-span-5 lg:col-start-8 lg:self-end">
            <Reveal delay={0.14}>
              <p className="editorial-sm" data-testid="founders-subheading">
                {FOUNDERS_SECTION.subheading}
              </p>
              <p className="body-muted measure mt-5 text-[14px] leading-[1.65]" data-testid="founders-intro">
                {FOUNDERS_SECTION.intro}
              </p>
            </Reveal>
          </div>
        </div>

        {/* the two founders - matched blue mats, identical heights, side by side */}
        <div className="mt-[clamp(30px,3.2vw,52px)] grid items-start gap-x-12 gap-y-12 sm:grid-cols-2">
          {FOUNDERS.map((founder, i) => (
            <FounderProfile key={founder.name} founder={founder} index={i} />
          ))}
        </div>

        {/* closing statement */}
        <div
          className="mt-[clamp(32px,3.4vw,56px)] border-t pt-9"
          style={{ borderColor: "rgba(23,21,34,0.12)" }}
          data-testid="founders-closing"
        >
          <div className="grid gap-y-7 lg:grid-cols-12 lg:gap-x-14">
            <div className="lg:col-span-7">
              <Reveal>
                <div className="space-y-5">
                  {FOUNDERS_SECTION.closing.map((paragraph) => (
                    <p key={paragraph} className="body-muted text-[15px] leading-[1.65]">
                      {paragraph}
                    </p>
                  ))}
                </div>
              </Reveal>
            </div>
            <div className="lg:col-span-5 lg:col-start-8 lg:self-center">
              <Reveal delay={0.1}>
                <p className="editorial-sm" data-testid="founders-signature" style={{ color: "#352A59" }}>
                  {FOUNDERS_SECTION.signature}
                </p>
              </Reveal>
            </div>
          </div>
        </div>

        {/* IBMCT crowdfunding collaboration note */}
        <Reveal delay={0.1}>
          <div
            className="mt-[clamp(28px,3vw,48px)] border-t pt-8"
            style={{ borderColor: "rgba(216,183,122,0.45)" }}
            data-testid="founders-ibmct-note"
          >
            <div className="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
              <div>
                <span className="eyebrow">In Collaboration With</span>
                <p className="editorial-sm mt-3" data-testid="founders-ibmct-heading">
                  {COLLABORATION.shortTitle}
                </p>
                <p className="body-muted mt-3 max-w-[62ch] text-[14px] leading-[1.6]">
                  {COLLABORATION.shortNote}
                </p>
              </div>
              <ArrowLink
                href={COLLABORATION.url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="founders-ibmct-link"
                className="shrink-0"
              >
                {COLLABORATION.linkLabel}
              </ArrowLink>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
